import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { RoomConfigFormState, RoomTypeCatalogItem, RoomTypeConfig } from "../model/types";

import { buildRoomConfigDrawerConfig } from "../lib/roomConfigDrawerFields";
import { defaultRoomConfigFormState, mapConfigToFormState, mapFormStateToPayload } from "../lib/roomConfigForm";

type UseRoomConfigFormOptions = {
  catalog?: readonly RoomTypeCatalogItem[];
  enabled: boolean;
  initialConfig?: null | RoomTypeConfig;
  isLoading: boolean;
};

export const useRoomConfigForm = ({ catalog = [], enabled, initialConfig, isLoading }: UseRoomConfigFormOptions) => {
  const { t } = useTranslation();
  const [formState, setFormState] = useState<RoomConfigFormState>(defaultRoomConfigFormState());

  useEffect(() => {
    if (!enabled || isLoading) return;
    setFormState(initialConfig ? mapConfigToFormState(initialConfig) : defaultRoomConfigFormState());
  }, [enabled, initialConfig, isLoading]);

  const updateField = useCallback((field: keyof RoomConfigFormState, value: unknown) => {
    setFormState((current) => ({ ...current, [field]: value }));
  }, []);

  const { fields, tabs } = useMemo(
    () => buildRoomConfigDrawerConfig({ catalog, formState, onFieldChange: updateField, t }),
    [catalog, formState, t, updateField]
  );

  return {
    fields,
    formState,
    mapPayload: () => mapFormStateToPayload(formState),
    tabs,
  };
};
